import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import { toast } from 'react-hot-toast';
import FilterDropdown, { FilterSection } from '../../components/FilterDropdown';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import TableColumnFilter from '../../components/TableColumnFilter';
import { durationPresets, getDurationPresetRange, getDurationLabel, DurationPreset } from '../dashboard/utils/dateHelpers';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { usePersistListFiltersSession } from '../../hooks/usePersistListFiltersSession';
import { LIST_FILTER_SESSION_KEYS } from '../../constants/listFilterSessionKeys';
import { isTicketSearchInputValid } from '../../utils/searchTerms';
import {
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  FireIcon,
  WrenchIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  EyeIcon,
  UserIcon,
  FolderIcon
} from '@heroicons/react/24/outline';

interface Case {
  id: string;
  _id?: string;
  number: string;
  shortDescription: string;
  description?: string;
  priority: string;
  state: string;
  problemState?: string;
  changeState?: string;
  category?: string;
  ticketType: string;
  assignedTo?: {
    id?: string;
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    name?: string;
  } | null;
  caller?: {
    id?: string;
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    name?: string;
  } | null;
  Customer?: {
    id?: string;
    accountName?: string;
    accountShortName?: string;
    emailAddress?: string;
  } | null;
  Engagement?: {
    id?: string;
    name?: string;
    status?: string;
  } | null;
  createdBy?: {
    id?: string;
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    name?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  pendingApprovals?: Array<{
    id: string;
    approverId: string;
    state: string;
    comments?: string;
  }>;
}

const priorityColors: Record<string, string> = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200',
  // Legacy support
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW: 'bg-blue-100 text-blue-800 border-blue-200',
  NORMAL: 'bg-gray-100 text-gray-800 border-gray-200'
};

const priorityLabels: Record<string, string> = {
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning',
  // Legacy support
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  NORMAL: 'Planning'
};

const ticketTypeIcons = {
  INCIDENT: <FireIcon className="h-5 w-5 text-red-500" />,
  PROBLEM: <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />,
  CHANGE: <ArrowPathIcon className="h-5 w-5 text-blue-500" />,
  SERVICE_REQUEST: <DocumentTextIcon className="h-5 w-5 text-green-500" />,
  CASE: <WrenchIcon className="h-5 w-5 text-gray-500" />
};

const ticketTypeColors = {
  INCIDENT: 'bg-red-50 text-red-700 border-red-300',
  PROBLEM: 'bg-orange-50 text-orange-700 border-orange-300',
  CHANGE: 'bg-blue-50 text-blue-700 border-blue-300',
  SERVICE_REQUEST: 'bg-green-50 text-green-700 border-green-300',
  CASE: 'bg-gray-50 text-gray-700 border-gray-300'
};


const stateColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800',
  open: 'bg-indigo-100 text-indigo-800',
  awaiting_info: 'bg-purple-100 text-purple-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  solution_proposed: 'bg-teal-100 text-teal-800',
  closed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
  in_progress: 'bg-green-100 text-green-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  pending_approval: 'bg-amber-100 text-amber-800'
};

const getTicketPermissionResource = (ticketType: string): string => {
  switch (ticketType) {
    case 'INCIDENT':
      return 'incidents';
    case 'SERVICE_REQUEST':
      return 'service_requests';
    case 'CHANGE':
      return 'changes';
    case 'PROBLEM':
      return 'problems';
    case 'CASE':
      return 'cases';
    default:
      return 'tickets';
  }
};

const getAgentViewTooltip = (ticketType: string): string => {
  const moduleLabels: Record<string, string> = {
    INCIDENT: 'Incidents',
    PROBLEM: 'Problems',
    CHANGE: 'Changes',
    SERVICE_REQUEST: 'Service Requests',
    CASE: 'Cases',
  };
  const moduleName = moduleLabels[ticketType] || 'module';
  return `Agent view — open in ${moduleName}`;
};

const getTicketModulePath = (ticketType: string, id: string): string => {
  switch (ticketType) {
    case 'INCIDENT':
      return `/incidents/${id}`;
    case 'SERVICE_REQUEST':
      return `/service-requests/${id}`;
    case 'CHANGE':
      return `/changes/${id}`;
    case 'PROBLEM':
      return `/problems/${id}`;
    case 'CASE':
      return `/cases/${id}`;
    default:
      return `/tickets/${id}`;
  }
};

const getStateLabel = (ticketType: string, state: string, problemState?: string, changeState?: string): string => {
  // For problems, show problemState if available
  if (ticketType === 'PROBLEM') {
    if (problemState) {
      const problemStateLabels: Record<string, string> = {
        new: 'New',
        assess_1: 'Assess 1',
        assess_2: 'Assess 2',
        root_cause_analysis: 'Root Cause Analysis',
        resolved: 'Resolved',
        closed: 'Closed'
      };
      return problemStateLabels[problemState] || problemState;
    }
  }

  // For changes, show changeState if available
  if (ticketType === 'CHANGE') {
    if (changeState) {
      const changeStateLabels: Record<string, string> = {
        new: 'New',
        assess: 'Assess',
        authorize: 'Authorize',
        scheduled: 'Scheduled',
        implement: 'Implement',
        review: 'Review',
        closed: 'Closed',
        cancelled: 'Cancelled'
      };
      return changeStateLabels[changeState] || changeState;
    }
    const changeStateLabels: Record<string, string> = {
      new: 'New',
      assess: 'Assess',
      authorize: 'Authorize',
      scheduled: 'Scheduled',
      implement: 'Implement',
      review: 'Review',
      closed: 'Closed',
      cancelled: 'Cancelled'
    };
    if (changeStateLabels[state]) {
      return changeStateLabels[state];
    }
    if (state === 'open') {
      return 'Closed';
    }
  }

  // Default state labels
  const stateLabels: Record<string, string> = {
    new: 'New',
    open: 'Open',
    awaiting_info: 'Awaiting Info',
    on_hold: 'On Hold',
    solution_proposed: 'Solution Proposed',
    in_progress: 'In Progress',
    resolved: 'Resolved',
    closed: 'Closed',
    cancelled: 'Cancelled',
    pending_approval: 'Pending Approval',
    approved: 'Approved',
    closed_complete: 'Closed Complete',
    closed_incomplete: 'Closed Incomplete',
    skipped: 'Skipped'
  };

  return stateLabels[state] || state;
};

// Helper function to strip HTML tags and decode HTML entities
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Filter options
const stateOptions = [
  { value: 'new', label: 'New' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const priorityOptions = [
  { value: 'critical_1', label: 'Critical' },
  { value: 'high_2', label: 'High' },
  { value: 'moderate_3', label: 'Moderate' },
  { value: 'low_4', label: 'Low' },
  { value: 'planning_5', label: 'Planning' },
];

const ticketTypeOptions = [
  { value: 'INCIDENT', label: 'Incidents' },
  { value: 'PROBLEM', label: 'Problems' },
  { value: 'CHANGE', label: 'Changes' },
  { value: 'SERVICE_REQUEST', label: 'Service Requests' },
  { value: 'CASE', label: 'Cases' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const Tickets: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { formatDate } = useDateFormatter();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  usePersistListFiltersSession('/tickets', LIST_FILTER_SESSION_KEYS.tickets);

  // Subscribe to real-time ticket events via SSE (all ticket types)
  useTicketEvents();

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearchTerm = searchParams.get('search') ?? '';

  useEffect(() => {
    setSearchInput(appliedSearchTerm);
  }, [appliedSearchTerm]);

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '25', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 25;
  }, [searchParams]);

  const filterState = useMemo(
    () => (searchParams.get('state') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterPriority = useMemo(
    () => (searchParams.get('priority') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterTicketType = useMemo(
    () => (searchParams.get('types') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterPendingMyApproval = searchParams.get('pending') === '1';
  const filterCustomer = searchParams.get('customer') ?? '';
  const filterAssignedTo = searchParams.get('assignedTo') ?? '';
  const filterUnassigned = searchParams.get('unassigned') === 'true';
  const filterNumber = searchParams.get('number') ?? '';
  const filterDescription = searchParams.get('desc') ?? '';

  const effectiveSearch = appliedSearchTerm || filterNumber || filterDescription;

  /** URL: omit duration = default this_month; duration=all = all time; else preset or custom:… */
  const filterDuration = useMemo(() => {
    const d = searchParams.get('duration');
    if (d === 'all') return '';
    if (d === null || d === '') return 'this_month';
    return d;
  }, [searchParams]);

  const { isContactUser, canUpdateEntity } = useUserPermissions();

  const { data: customersData } = useQuery({
    queryKey: ['customers-for-ticket-filter'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    },
    enabled: !isContactUser,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users-for-ticket-filter'],
    queryFn: async () => {
      const response = await api.get('/users?limit=500&class=user');
      return response.data;
    },
    enabled: !isContactUser,
  });

  const customerOptions = useMemo(
    () =>
      customersData?.customers?.map((c: { id: string; accountShortName?: string; accountName?: string }) => ({
        value: c.id,
        label: c.accountShortName || c.accountName || c.id,
      })) ?? [],
    [customersData]
  );

  const assignedToOptions = useMemo(() => {
    const userOptions =
      usersData?.users?.map((u: { id: string; firstName?: string; lastName?: string; email?: string }) => ({
        value: u.id,
        label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u.id,
      })) ?? [];
    return [{ value: '__unassigned__', label: 'Unassigned' }, ...userOptions];
  }, [usersData]);

  // Get browser's timezone offset (in minutes from UTC)
  // This is used to ensure date filtering is consistent with user's local timezone
  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);

  // Calculate date range from duration filter (needs to be before queries that use it)
  const dateRange = useMemo(() => {
    if (!filterDuration) return { dateFrom: '', dateTo: '' };

    // Handle custom range format: "custom:YYYY-MM-DD,YYYY-MM-DD"
    if (filterDuration.startsWith('custom:')) {
      const [, dates] = filterDuration.split('custom:');
      const [from, to] = dates.split(',');
      return { dateFrom: from || '', dateTo: to || '' };
    }

    // Handle preset values
    const range = getDurationPresetRange(filterDuration as DurationPreset);
    return { dateFrom: range.from, dateTo: range.to };
  }, [filterDuration]);

  // Fetch ticket type counts for stats bar (with date filter support)
  const { data: ticketStats } = useQuery({
    queryKey: ['ticket-stats', isContactUser, dateRange.dateFrom, dateRange.dateTo, timezoneOffset],
    queryFn: async () => {
      // Fetch counts for each ticket type in parallel
      const ticketTypes = ['INCIDENT', 'PROBLEM', 'CHANGE', 'SERVICE_REQUEST', 'CASE'];

      // Build query params with date filters and timezone offset
      const dateParams = new URLSearchParams();
      if (dateRange.dateFrom) dateParams.set('dateFrom', dateRange.dateFrom);
      if (dateRange.dateTo) dateParams.set('dateTo', dateRange.dateTo);
      // Always include timezone offset when date filters are present
      if (dateRange.dateFrom || dateRange.dateTo) {
        dateParams.set('timezoneOffset', String(timezoneOffset));
      }
      const dateQueryStr = dateParams.toString();

      const results = await Promise.all(
        ticketTypes.map(async (type) => {
          try {
            const url = `/cases/stats?ticketType=${type}${dateQueryStr ? `&${dateQueryStr}` : ''}`;
            const response = await api.get(url);
            return { type, total: response.data.total || 0 };
          } catch {
            return { type, total: 0 };
          }
        })
      );

      const stats: Record<string, number> = {};
      results.forEach(({ type, total }) => {
        stats[type] = total;
      });
      return stats;
    },
    refetchOnWindowFocus: false,
    staleTime: 30000
  });


  const { data, isLoading, error } = useQuery({
    queryKey: ['all-cases', page, pageSize, effectiveSearch, filterState, filterPriority, filterTicketType, filterPendingMyApproval, filterDuration, filterCustomer, filterAssignedTo, filterUnassigned, isContactUser, timezoneOffset],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(effectiveSearch && { search: effectiveSearch }),
        ...(filterState.length > 0 && { state: filterState.join(',') }),
        ...(filterPriority.length > 0 && { priority: filterPriority.join(',') }),
        ...(filterTicketType.length > 0 && { ticketType: filterTicketType.join(',') }),
        ...(filterPendingMyApproval && { pendingMyApproval: 'true' }),
        ...(filterCustomer && { customerId: filterCustomer }),
        ...(filterAssignedTo && { assignedToId: filterAssignedTo }),
        ...(filterUnassigned && { unassigned: 'true' }),
        ...(dateRange.dateFrom && { dateFrom: dateRange.dateFrom }),
        ...(dateRange.dateTo && { dateTo: dateRange.dateTo }),
        // Include timezone offset for proper date filtering when date filters are present
        ...((dateRange.dateFrom || dateRange.dateTo) && { timezoneOffset: String(timezoneOffset) })
      });

      // Use 'ALL' to get all ticket types with proper server-side pagination
      // This ensures correct total counts and pagination across all ticket types
      if (filterTicketType.length === 0) {
        params.set('ticketType', 'ALL');
      }

      const response = await api.get(`/cases?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0
  });

  // Mutation to respond to approval
  const respondToApprovalMutation = useMutation({
    mutationFn: async ({ approvalId, status }: { approvalId: string; status: 'approved' | 'rejected' }) => {
      const response = await api.patch(`/approvals/${approvalId}/respond`, {
        status
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Approval response submitted successfully');
      queryClient.invalidateQueries({ queryKey: ['all-cases'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to respond to approval');
    }
  });

  const handleApprovalResponse = (approvalId: string, status: 'approved' | 'rejected') => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Are you sure you want to ${status === 'approved' ? 'approve' : 'reject'} this ticket fix?`)) {
      respondToApprovalMutation.mutate({ approvalId, status });
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({
      search: searchInput.trim() || null,
      page: '1',
    });
  };

  // Duration filter options (excluding custom as FilterDropdown adds it automatically for time-range)
  const durationOptions = useMemo(() =>
    durationPresets.filter(p => p.value !== 'custom').map(p => ({
      value: p.value,
      label: p.label
    })),
  []);

  // Build filter sections for FilterDropdown
  const filterSections: FilterSection[] = useMemo(() => [
    {
      id: 'duration',
      label: 'Duration',
      type: 'time-range',
      options: durationOptions,
      placeholder: 'All Time',
    },
    {
      id: 'ticketType',
      label: 'Ticket Type',
      type: 'multi-select',
      options: ticketTypeOptions,
      placeholder: 'All Types',
    },
    {
      id: 'state',
      label: 'State',
      type: 'multi-select',
      options: stateOptions,
      placeholder: 'All States',
    },
    {
      id: 'priority',
      label: 'Priority',
      type: 'multi-select',
      options: priorityOptions,
      placeholder: 'All Priorities',
    },
    {
      id: 'pendingMyApproval',
      label: 'Pending My Approval',
      type: 'single-select',
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
      placeholder: 'All',
    },
  ], [durationOptions]);

  // Build active filter chips
  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (appliedSearchTerm) {
      chips.push({
        id: 'search',
        label: 'Search',
        value: `"${appliedSearchTerm}"`,
        color: 'blue',
      });
    }

    if (filterNumber) {
      chips.push({
        id: 'number',
        label: 'Number',
        value: `"${filterNumber}"`,
        color: 'blue',
      });
    }

    if (filterDescription) {
      chips.push({
        id: 'desc',
        label: 'Description',
        value: `"${filterDescription}"`,
        color: 'blue',
      });
    }

    if (filterCustomer) {
      const customer = customersData?.customers?.find((c: { id: string }) => c.id === filterCustomer);
      chips.push({
        id: 'customer',
        label: 'Customer',
        value: customer?.accountShortName || customer?.accountName || filterCustomer,
        color: 'cyan',
      });
    }

    if (filterTicketType.length > 0) {
      const typeLabels = filterTicketType.map(t =>
        ticketTypeOptions.find(o => o.value === t)?.label || t
      );
      chips.push({
        id: 'ticketType',
        label: 'Type',
        value: typeLabels.join(', '),
        color: 'cyan',
      });
    }

    if (filterState.length > 0) {
      const stateLabels = filterState.map(s =>
        stateOptions.find(o => o.value === s)?.label || s
      );
      chips.push({
        id: 'state',
        label: 'State',
        value: stateLabels.join(', '),
        color: 'purple',
      });
    }

    if (filterPriority.length > 0) {
      const priorityLabelsList = filterPriority.map(p =>
        priorityOptions.find(o => o.value === p)?.label || p
      );
      chips.push({
        id: 'priority',
        label: 'Priority',
        value: priorityLabelsList.join(', '),
        color: 'orange',
      });
    }

    if (filterPendingMyApproval) {
      chips.push({
        id: 'pendingMyApproval',
        label: 'Pending My Approval',
        value: 'Yes',
        color: 'teal',
      });
    }

    if (filterUnassigned) {
      chips.push({
        id: 'unassigned',
        label: 'Assigned To',
        value: 'Unassigned',
        color: 'gray',
      });
    } else if (filterAssignedTo) {
      const user = usersData?.users?.find((u: { id: string }) => u.id === filterAssignedTo);
      chips.push({
        id: 'assignedTo',
        label: 'Assigned To',
        value: user
          ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || filterAssignedTo
          : filterAssignedTo,
        color: 'gray',
      });
    }

    if (filterDuration) {
      // Get display label for duration filter
      let durationLabel = '';
      if (filterDuration.startsWith('custom:')) {
        const [, dates] = filterDuration.split('custom:');
        const [from, to] = dates.split(',');
        durationLabel = getDurationLabel('custom', from, to);
      } else {
        durationLabel = getDurationLabel(filterDuration as DurationPreset);
      }
      chips.push({
        id: 'duration',
        label: 'Duration',
        value: durationLabel,
        color: 'green',
      });
    }

    return chips;
  }, [appliedSearchTerm, filterNumber, filterDescription, filterTicketType, filterState, filterPriority, filterPendingMyApproval, filterDuration, filterCustomer, filterAssignedTo, filterUnassigned, customersData, usersData]);

  // Handle filter dropdown changes
  const handleFilterChange = (sectionId: string, value: string | string[]) => {
    switch (sectionId) {
      case 'duration': {
        const v = value as string;
        if (!v) {
          patchSearchParams({ duration: 'all', page: '1' });
        } else if (v === 'this_month') {
          patchSearchParams({ duration: null, page: '1' });
        } else {
          patchSearchParams({ duration: v, page: '1' });
        }
        break;
      }
      case 'ticketType': {
        const arr = value as string[];
        patchSearchParams({
          types: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'state': {
        const arr = value as string[];
        patchSearchParams({
          state: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'priority': {
        const arr = value as string[];
        patchSearchParams({
          priority: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'pendingMyApproval':
        patchSearchParams({
          pending: value === 'true' ? '1' : null,
          page: '1',
        });
        break;
    }
  };

  // Handle removing a single filter chip
  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'number':
        patchSearchParams({ number: null, page: '1' });
        break;
      case 'desc':
        patchSearchParams({ desc: null, page: '1' });
        break;
      case 'customer':
        patchSearchParams({ customer: null, page: '1' });
        break;
      case 'assignedTo':
        patchSearchParams({ assignedTo: null, page: '1' });
        break;
      case 'unassigned':
        patchSearchParams({ unassigned: null, page: '1' });
        break;
      case 'duration':
        patchSearchParams({ duration: 'all', page: '1' });
        break;
      case 'ticketType':
        patchSearchParams({ types: null, page: '1' });
        break;
      case 'state':
        patchSearchParams({ state: null, page: '1' });
        break;
      case 'priority':
        patchSearchParams({ priority: null, page: '1' });
        break;
      case 'pendingMyApproval':
        patchSearchParams({ pending: null, page: '1' });
        break;
    }
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      number: null,
      desc: null,
      state: null,
      priority: null,
      types: null,
      pending: null,
      customer: null,
      assignedTo: null,
      unassigned: null,
      duration: 'all',
      page: '1',
    });
  };

  const handleAssignedToColumnFilter = (value: string | string[]) => {
    const v = typeof value === 'string' ? value : '';
    if (v === '__unassigned__') {
      patchSearchParams({ assignedTo: null, unassigned: 'true', page: '1' });
    } else if (v) {
      patchSearchParams({ assignedTo: v, unassigned: null, page: '1' });
    } else {
      patchSearchParams({ assignedTo: null, unassigned: null, page: '1' });
    }
  };

  const assignedToColumnValue = filterUnassigned ? '__unassigned__' : filterAssignedTo;

  if (isLoading) {
    return (
      <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading cases. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <form onSubmit={handleSearch} className="flex items-center justify-between gap-4">
            {/* Left side: Title and Filter dropdown */}
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-bold text-gray-900 whitespace-nowrap">Tickets</h1>
              <FilterDropdown
                sections={filterSections}
                values={{
                  duration: filterDuration,
                  ticketType: filterTicketType,
                  state: filterState,
                  priority: filterPriority,
                  pendingMyApproval: filterPendingMyApproval ? 'true' : '',
                }}
                onChange={handleFilterChange}
              />
            </div>

            {/* Right side: Search */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search tickets or numbers (comma/space separated)..."
                  className="pl-9 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={!isTicketSearchInputValid(searchInput)}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Stats Summary Bar */}
      <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5">
              <FolderIcon className="h-4 w-4 text-gray-500" />
              <span className="text-gray-600">Cases:</span>
              <span className="font-semibold text-gray-900">{ticketStats?.CASE ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <FireIcon className="h-4 w-4 text-red-500" />
              <span className="text-gray-600">Incidents:</span>
              <span className="font-semibold text-gray-900">{ticketStats?.INCIDENT ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <ExclamationTriangleIcon className="h-4 w-4 text-orange-500" />
              <span className="text-gray-600">Problems:</span>
              <span className="font-semibold text-gray-900">{ticketStats?.PROBLEM ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <DocumentTextIcon className="h-4 w-4 text-green-500" />
              <span className="text-gray-600">Service Requests:</span>
              <span className="font-semibold text-gray-900">{ticketStats?.SERVICE_REQUEST ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <ArrowPathIcon className="h-4 w-4 text-blue-500" />
              <span className="text-gray-600">Changes:</span>
              <span className="font-semibold text-gray-900">{ticketStats?.CHANGE ?? '-'}</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-600">
              Total: <span className="font-semibold text-gray-900">{data?.total ?? '-'}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Active Filters Indicator */}
      {activeFilterChips.length > 0 && (
        <div className="mb-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      {/* Tickets Table */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
          <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  <TableColumnFilter
                    label="Type"
                    type="multi-select"
                    options={ticketTypeOptions}
                    value={filterTicketType}
                    placeholder="All Types"
                    onChange={(v) => {
                      const arr = Array.isArray(v) ? v : [];
                      patchSearchParams({ types: arr.length > 0 ? arr.join(',') : null, page: '1' });
                    }}
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  <TableColumnFilter
                    label="Number"
                    type="text"
                    value={filterNumber}
                    placeholder="Filter by number (comma or space separated)..."
                    onChange={(v) => {
                      patchSearchParams({ number: typeof v === 'string' && v ? v : null, page: '1' });
                    }}
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  <TableColumnFilter
                    label="Description"
                    type="text"
                    value={filterDescription}
                    placeholder="Filter by description..."
                    onChange={(v) => {
                      patchSearchParams({ desc: typeof v === 'string' && v ? v : null, page: '1' });
                    }}
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {!isContactUser ? (
                    <TableColumnFilter
                      label="Customer"
                      type="searchable-select"
                      options={customerOptions}
                      value={filterCustomer}
                      placeholder="All Customers"
                      onChange={(v) => {
                        patchSearchParams({ customer: typeof v === 'string' && v ? v : null, page: '1' });
                      }}
                    />
                  ) : (
                    'Customer'
                  )}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  <TableColumnFilter
                    label="Priority"
                    type="multi-select"
                    options={priorityOptions}
                    value={filterPriority}
                    placeholder="All Priorities"
                    onChange={(v) => {
                      const arr = Array.isArray(v) ? v : [];
                      patchSearchParams({ priority: arr.length > 0 ? arr.join(',') : null, page: '1' });
                    }}
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {!isContactUser ? (
                    <TableColumnFilter
                      label="Assigned To"
                      type="searchable-select"
                      options={assignedToOptions}
                      value={assignedToColumnValue}
                      placeholder="All Users"
                      onChange={handleAssignedToColumnFilter}
                    />
                  ) : (
                    'Assigned To'
                  )}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  <TableColumnFilter
                    label="State"
                    type="multi-select"
                    options={stateOptions}
                    value={filterState}
                    placeholder="All States"
                    onChange={(v) => {
                      const arr = Array.isArray(v) ? v : [];
                      patchSearchParams({ state: arr.length > 0 ? arr.join(',') : null, page: '1' });
                    }}
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  <TableColumnFilter
                    label="Created"
                    type="time-range"
                    options={[
                      ...durationOptions,
                      { value: 'all', label: 'All Time' },
                    ]}
                    value={filterDuration || 'all'}
                    placeholder="All Time"
                    active={searchParams.has('duration')}
                    onChange={(v) => {
                      const val = typeof v === 'string' ? v : '';
                      if (!val || val === 'all') {
                        patchSearchParams({ duration: 'all', page: '1' });
                      } else if (val === 'this_month') {
                        patchSearchParams({ duration: null, page: '1' });
                      } else {
                        patchSearchParams({ duration: val, page: '1' });
                      }
                    }}
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.cases && data.cases.length > 0 ? data.cases.map((caseItem: Case) => (
                <tr key={caseItem.id || caseItem._id || caseItem.number} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center">
                      {ticketTypeIcons[caseItem.ticketType as keyof typeof ticketTypeIcons]}
                      <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full border ${ticketTypeColors[caseItem.ticketType as keyof typeof ticketTypeColors]}`}>
                        {caseItem.ticketType.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                    <Link
                      to={`/tickets/${caseItem.id || caseItem._id || ''}`}
                      state={{ ticketsListSearch: location.search }}
                      className="text-indigo-600 hover:text-indigo-900 hover:underline"
                      title="View ticket details"
                    >
                      {caseItem.number}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    <div className="max-w-xs truncate">
                      {stripHtml(caseItem.shortDescription)}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                    {caseItem.Customer ? (
                      <div>
                        <div className="font-medium">{caseItem.Customer.accountShortName || caseItem.Customer.accountName}</div>
                      </div>
                    ) : (
                      <span className="text-gray-500">No Customer</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[caseItem.priority] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                      {priorityLabels[caseItem.priority] || caseItem.priority}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                    {caseItem.assignedTo && (caseItem.assignedTo.firstName || caseItem.assignedTo.email) ? (
                      <>
                        {`${[caseItem.assignedTo.firstName, caseItem.assignedTo.lastName].filter(Boolean).join(' ') || caseItem.assignedTo.email}`}
                      </>
                    ) : (
                      <span className="text-gray-500">Unassigned</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stateColors[caseItem.state] || 'bg-gray-100 text-gray-800'}`}>
                      {getStateLabel(caseItem.ticketType, caseItem.state, caseItem.problemState, caseItem.changeState)}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(caseItem.createdAt)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {/* Show approve/reject buttons if user has pending approval for this ticket */}
                      {caseItem.pendingApprovals && caseItem.pendingApprovals.length > 0 && user && caseItem.pendingApprovals.some(approval => approval.approverId === user.id) && (
                        <>
                          <button
                            onClick={() => {
                              const userApproval = caseItem.pendingApprovals?.find(a => a.approverId === user.id);
                              if (userApproval) {
                                handleApprovalResponse(userApproval.id, 'approved');
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 border border-green-300"
                            title="Approve this ticket fix"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              const userApproval = caseItem.pendingApprovals?.find(a => a.approverId === user.id);
                              if (userApproval) {
                                handleApprovalResponse(userApproval.id, 'rejected');
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 border border-red-300"
                            title="Reject this ticket fix"
                          >
                            <XCircleIcon className="h-4 w-4" />
                            Reject
                          </button>
                        </>
                      )}
                      {canUpdateEntity(getTicketPermissionResource(caseItem.ticketType)) && (
                        <div className="relative group inline-flex items-center">
                          <Link
                            to={getTicketModulePath(caseItem.ticketType, caseItem.id || caseItem._id || '')}
                            className="text-gray-600 hover:text-gray-900 inline-flex items-center"
                            aria-label={getAgentViewTooltip(caseItem.ticketType)}
                          >
                            <UserIcon className="h-5 w-5" />
                          </Link>
                          <div
                            role="tooltip"
                            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none shadow-lg"
                          >
                            {getAgentViewTooltip(caseItem.ticketType)}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                          </div>
                        </div>
                      )}
                      <Link
                        to={`/tickets/${caseItem.id || caseItem._id || ''}`}
                        state={{ ticketsListSearch: location.search }}
                        className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                        title="View ticket details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-gray-500">
                    No tickets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {data?.cases && data.cases.length > 0 ? (
        <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 py-2 rounded-lg shadow flex-shrink-0" style={{ minHeight: '48px' }}>
          {/* Left: Records per page dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                patchSearchParams({
                  pageSize: e.target.value,
                  page: '1',
                });
              }}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {PAGE_SIZE_OPTIONS.map((size, index) => {
                const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                const isDisabled = data.total ? previousSize >= data.total && size > data.total : false;
                return (
                  <option key={size} value={size} disabled={isDisabled}>
                    {size}
                  </option>
                );
              })}
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          {/* Center: Entry count */}
          <div className="text-sm text-gray-700">
            Showing {Math.min((page - 1) * pageSize + 1, data.total || 0)} to {Math.min(page * pageSize, data.total || 0)} of {data.total || 0} entries
          </div>

          {/* Right: Pagination buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => patchSearchParams({ page: '1' })}
              disabled={page === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First page"
            >
              &laquo;
            </button>
            <button
              onClick={() => patchSearchParams({ page: String(page - 1) })}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {/* Page numbers */}
            {(() => {
              const totalPages = Math.ceil((data.total || 0) / pageSize);
              const pages: (number | string)[] = [];

              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                if (page <= 3) {
                  pages.push(1, 2, 3, 4, '...', totalPages);
                } else if (page >= totalPages - 2) {
                  pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                }
              }

              return pages.map((p, idx) => (
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => patchSearchParams({ page: String(p) })}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      page === p
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              ));
            })()}

            <button
              onClick={() => patchSearchParams({ page: String(page + 1) })}
              disabled={page >= Math.ceil((data.total || 0) / pageSize)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() =>
                patchSearchParams({ page: String(Math.ceil((data.total || 0) / pageSize)) })
              }
              disabled={page >= Math.ceil((data.total || 0) / pageSize)}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last page"
            >
              &raquo;
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Tickets;
